"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.addEventProcessor = addEventProcessor;
exports.captureCheckIn = captureCheckIn;
exports.captureEvent = captureEvent;
exports.captureException = captureException;
exports.captureMessage = captureMessage;
exports.captureSession = captureSession;
exports.close = close;
exports.endSession = endSession;
exports.flush = flush;
exports.isEnabled = isEnabled;
exports.isInitialized = isInitialized;
exports.lastEventId = lastEventId;
exports.setContext = setContext;
exports.setExtra = setExtra;
exports.setExtras = setExtras;
exports.setTag = setTag;
exports.setTags = setTags;
exports.setUser = setUser;
exports.startSession = startSession;
exports.withMonitor = withMonitor;

var _constants = require("./constants.js");

var _currentScopes = require("./currentScopes.js");

var _debugBuild = require("./debug-build.js");

var _session = require("./session.js");

var _is = require("./utils-hoist/is.js");

var _logger = require("./utils-hoist/logger.js");

var _misc = require("./utils-hoist/misc.js");

var _time = require("./utils-hoist/time.js");

var _worldwide = require("./utils-hoist/worldwide.js");

var _prepareEvent = require("./utils/prepareEvent.js");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * Captures an exception event and sends it to Sentry.
 *
 * @param exception The exception to capture.
 * @param hint Optional additional data to attach to the Sentry event.
 * @returns the id of the captured Sentry event.
 */
function captureException(exception, hint) {
  return (0, _currentScopes.getCurrentScope)().captureException(exception, (0, _prepareEvent.parseEventHintOrCaptureContext)(hint));
}
/**
 * Captures a message event and sends it to Sentry.
 *
 * @param message The message to send to Sentry.
 * @param captureContext Define the level of the message or pass in additional data to attach to the message.
 * @returns the id of the captured message.
 */


function captureMessage(message, captureContext) {
  // This is necessary to provide explicit scopes upgrade, without changing the original
  // arity of the `captureMessage(message, level)` method.
  const level = typeof captureContext === 'string' ? captureContext : undefined;
  const context = typeof captureContext !== 'string' ? {
    captureContext
  } : undefined;
  return (0, _currentScopes.getCurrentScope)().captureMessage(message, level, context);
}
/**
 * Captures a manually created event and sends it to Sentry.
 *
 * @param event The event to send to Sentry.
 * @param hint Optional additional data to attach to the Sentry event.
 * @returns the id of the captured event.
 */


function captureEvent(event, hint) {
  return (0, _currentScopes.getCurrentScope)().captureEvent(event, hint);
}
/**
 * Sets context data with the given name.
 * @param name of the context
 * @param context Any kind of data. This data will be normalized.
 */


function setContext(name, context) {
  (0, _currentScopes.getIsolationScope)().setContext(name, context);
}
/**
 * Set an object that will be merged sent as extra data with the event.
 * @param extras Extras object to merge into current context.
 */


function setExtras(extras) {
  (0, _currentScopes.getIsolationScope)().setExtras(extras);
}
/**
 * Set key:value that will be sent as extra data with the event.
 * @param key String of extra
 * @param extra Any kind of data. This data will be normalized.
 */


function setExtra(key, extra) {
  (0, _currentScopes.getIsolationScope)().setExtra(key, extra);
}
/**
 * Set an object that will be merged sent as tags data with the event.
 * @param tags Tags context object to merge into current context.
 */


function setTags(tags) {
  (0, _currentScopes.getIsolationScope)().setTags(tags);
}
/**
 * Set key:value that will be sent as tags data with the event.
 *
 * Can also be used to unset a tag, by passing `undefined`.
 *
 * @param key String key of tag
 * @param value Value of tag
 */


function setTag(key, value) {
  (0, _currentScopes.getIsolationScope)().setTag(key, value);
}
/**
 * Updates user context information for future events.
 *
 * @param user User context object to be set in the current context. Pass `null` to unset the user.
 */


function setUser(user) {
  (0, _currentScopes.getIsolationScope)().setUser(user);
}
/**
 * The last error event id of the isolation scope.
 *
 * Warning: This function really returns the last recorded error event id on the current
 * isolation scope. If you call this function after handling a certain error and another error
 * is captured in between, the last one is returned instead of the one you might expect.
 * Also, ids of events that were never sent to Sentry (for example because
 * they were dropped in `beforeSend`) could be returned.
 *
 * @returns The last event id of the isolation scope.
 */


function lastEventId() {
  return (0, _currentScopes.getIsolationScope)().lastEventId();
}
/**
 * Create a cron monitor check in and send it to Sentry.
 *
 * @param checkIn An object that describes a check in.
 * @param upsertMonitorConfig An optional object that describes a monitor config. Use this if you want
 * to create a monitor automatically when sending a check in.
 */


function captureCheckIn(checkIn, upsertMonitorConfig) {
  const scope = (0, _currentScopes.getCurrentScope)();
  const client = (0, _currentScopes.getClient)();

  if (!client) {
    _debugBuild.DEBUG_BUILD && _logger.logger.warn('Cannot capture check-in. No client defined.');
  } else if (!client.captureCheckIn) {
    _debugBuild.DEBUG_BUILD && _logger.logger.warn('Cannot capture check-in. Client does not support sending check-ins.');
  } else {
    return client.captureCheckIn(checkIn, upsertMonitorConfig, scope);
  }

  return (0, _misc.uuid4)();
}
/**
 * Wraps a callback with a cron monitor check in. The check in will be sent to Sentry when the callback finishes.
 *
 * @param monitorSlug The distinct slug of the monitor.
 * @param upsertMonitorConfig An optional object that describes a monitor config. Use this if you want
 * to create a monitor automatically when sending a check in.
 */


function withMonitor(monitorSlug, callback, upsertMonitorConfig) {
  const checkInId = captureCheckIn({
    monitorSlug,
    status: 'in_progress'
  }, upsertMonitorConfig);
  const now = (0, _time.timestampInSeconds)();

  function finishCheckIn(status) {
    captureCheckIn({
      monitorSlug,
      status,
      checkInId,
      duration: (0, _time.timestampInSeconds)() - now
    });
  }

  return (0, _currentScopes.withIsolationScope)(() => {
    let maybePromiseResult;

    try {
      maybePromiseResult = callback();
    } catch (e) {
      finishCheckIn('error');
      throw e;
    }

    if ((0, _is.isThenable)(maybePromiseResult)) {
      Promise.resolve(maybePromiseResult).then(() => {
        finishCheckIn('ok');
      }, e => {
        finishCheckIn('error');
        throw e;
      });
    } else {
      finishCheckIn('ok');
    }

    return maybePromiseResult;
  });
}
/**
 * Call `flush()` on the current client, if there is one. See {@link Client.flush}.
 *
 * @param timeout Maximum time in ms the client should wait to flush its event queue. Omitting this parameter will cause
 * the client to wait until all events are sent before resolving the promise.
 * @returns A promise which resolves to `true` if the queue successfully drains before the timeout, or `false` if it
 * doesn't (or if there's no client defined).
 */


async function flush(timeout) {
  const client = (0, _currentScopes.getClient)();

  if (client) {
    return client.flush(timeout);
  }

  _debugBuild.DEBUG_BUILD && _logger.logger.warn('Cannot flush events. No client defined.');
  return Promise.resolve(false);
}
/**
 * Call `close()` on the current client, if there is one. See {@link Client.close}.
 *
 * @param timeout Maximum time in ms the client should wait to flush its event queue before shutting down. Omitting this
 * parameter will cause the client to wait until all events are sent before disabling itself.
 * @returns A promise which resolves to `true` if the queue successfully drains before the timeout, or `false` if it
 * doesn't (or if there's no client defined).
 */


async function close(timeout) {
  const client = (0, _currentScopes.getClient)();

  if (client) {
    return client.close(timeout);
  }

  _debugBuild.DEBUG_BUILD && _logger.logger.warn('Cannot flush events and disable SDK. No client defined.');
  return Promise.resolve(false);
}
/**
 * Returns true if Sentry has been properly initialized.
 */


function isInitialized() {
  return !!(0, _currentScopes.getClient)();
}
/** If the SDK is initialized & enabled. */


function isEnabled() {
  const client = (0, _currentScopes.getClient)();
  return !!client && client.getOptions().enabled !== false && !!client.getTransport();
}
/**
 * Add an event processor.
 * This will be added to the current isolation scope, ensuring any event that is processed in the current execution
 * context will have the processor applied.
 */


function addEventProcessor(callback) {
  (0, _currentScopes.getIsolationScope)().addEventProcessor(callback);
}
/**
 * Start a session on the current isolation scope.
 *
 * @param context (optional) additional properties to be applied to the returned session object
 *
 * @returns the new active session
 */


function startSession(context) {
  const client = (0, _currentScopes.getClient)();
  const isolationScope = (0, _currentScopes.getIsolationScope)();
  const currentScope = (0, _currentScopes.getCurrentScope)();

  const _ref = client && client.getOptions() || {},
        release = _ref.release,
        _ref$environment = _ref.environment,
        environment = _ref$environment === void 0 ? _constants.DEFAULT_ENVIRONMENT : _ref$environment; // Will fetch userAgent if called from browser sdk


  const _ref2 = _worldwide.GLOBAL_OBJ.navigator || {},
        userAgent = _ref2.userAgent;

  const session = (0, _session.makeSession)(_objectSpread(_objectSpread({
    release,
    environment,
    user: currentScope.getUser() || isolationScope.getUser()
  }, userAgent && {
    userAgent
  }), context)); // End existing session if there's one

  const currentSession = isolationScope.getSession();

  if (currentSession && currentSession.status === 'ok') {
    (0, _session.updateSession)(currentSession, {
      status: 'exited'
    });
  }

  endSession(); // Afterwards we set the new session on the scope

  isolationScope.setSession(session); // TODO (v8): Remove this and only use the isolation scope(?).
  // For v7 though, we can't "soft-break" people using getCurrentHub().getScope().setSession()

  currentScope.setSession(session);
  return session;
}
/**
 * End the session on the current isolation scope.
 */


function endSession() {
  const isolationScope = (0, _currentScopes.getIsolationScope)();
  const currentScope = (0, _currentScopes.getCurrentScope)();
  const session = currentScope.getSession() || isolationScope.getSession();

  if (session) {
    (0, _session.closeSession)(session);
  }

  _sendSessionUpdate(); // the session is over; take it off of the scope


  isolationScope.setSession(); // TODO (v8): Remove this and only use the isolation scope(?).
  // For v7 though, we can't "soft-break" people using getCurrentHub().getScope().setSession()

  currentScope.setSession();
}
/**
 * Sends the current Session on the scope
 */


function _sendSessionUpdate() {
  const isolationScope = (0, _currentScopes.getIsolationScope)();
  const currentScope = (0, _currentScopes.getCurrentScope)();
  const client = (0, _currentScopes.getClient)(); // TODO (v8): Remove currentScope and only use the isolation scope(?).
  // For v7 though, we can't "soft-break" people using getCurrentHub().getScope().setSession()

  const session = currentScope.getSession() || isolationScope.getSession();

  if (session && client) {
    client.captureSession(session);
  }
}
/**
 * Sends the current session on the scope to Sentry
 *
 * @param end If set the session will be marked as exited and removed from the scope.
 *            Defaults to `false`.
 */


function captureSession(end = false) {
  // both send the update and pull the session from the scope
  if (end) {
    endSession();
    return;
  } // only send the update


  _sendSessionUpdate();
}